/**
 * Role helpers — single source of truth for role-based gating in the UI.
 *
 * Two roles grant admin-equivalent access:
 *   - "admin"      — the customer's own administrators (configured at signup
 *                    or via the engineers page).
 *   - "superadmin" — auto-seeded platform-support credential ("admin@cushty.io")
 *                    placed in every tenant by the provisioning workflow.
 *                    Used by Cushty platform staff to log into a tenant for
 *                    diagnostics without bothering the customer.
 *
 * The server's `requireAdmin` middleware (server/auth.ts) already accepts
 * either role. Historically the client UI used hardcoded
 * `user.role === "admin"` in dozens of places, which left a logged-in
 * superadmin staring at a stripped-down read-only UI even though the API
 * would have happily served them. This helper fixes that — every literal
 * comparison should use `isAdmin(user)` instead.
 *
 * What this helper does NOT do:
 *   - It is not a substitute for server enforcement. The server is still the
 *     source of truth (requireAdmin in server/auth.ts).
 *   - It does NOT include superadmin in lists of *customer-side* admins —
 *     e.g. the email recipient list when an engineer marks a job incomplete.
 *     Those continue to filter on `role === "admin"` literally in
 *     server/routes.ts so the platform support credential isn't spammed
 *     with customer-operational notifications.
 */

import type { User } from "@shared/schema";

const ADMIN_ROLES: ReadonlySet<string> = new Set(["admin", "superadmin"]);

/**
 * True iff the user has admin-equivalent privileges in the tenant UI.
 * Safe to call with `null`/`undefined` (returns false).
 */
export function isAdmin(user: Pick<User, "role"> | null | undefined): boolean {
  return user != null && ADMIN_ROLES.has(user.role);
}

/**
 * True iff the role string itself is admin-equivalent. Useful when you
 * already have a role string in hand (e.g. inside a list of users where
 * each row has its own role).
 */
export function isAdminRole(role: string | null | undefined): boolean {
  return role != null && ADMIN_ROLES.has(role);
}

/**
 * True iff this user is the platform-support superadmin specifically.
 * Use sparingly — only when the UI needs to indicate "you're logged in as
 * Cushty platform support" (e.g. a small banner). For permission gating,
 * use `isAdmin` so customer admins keep parity.
 */
export function isPlatformSuperadmin(
  user: Pick<User, "role"> | null | undefined,
): boolean {
  return user?.role === "superadmin";
}
